import { connect } from 'react-redux';
import { ApplicationState } from "../../../store";
import { ToxnotComponentProperties } from "../BaseApplicationComponents/ToxnotComponent";
import * as SurveyResponseController from "../../../store/StateControllers/Survey/SurveyResponse/SurveyResponseController";
import * as SurveyStateController from "../../../store/StateControllers/Survey/SurveyStateController";
import { SurveyResponseState } from '../../../store/StateControllers/Survey/SurveyResponse/SurveyResponseStates';
import { AISurveyAgentState, getActiveAIOperationFromState } from '../../../store/StateControllers/Survey/SurveyResponse/AISurveyAgentController';
import React, { useState } from 'react';
import TextInput from "../TextInput";
import TextAreaInput from "../TextAreaInput";
import { Result, ToxnotServiceClient } from "../../../store/ToxnotServiceClient";
import { ToxnotSignInRequiredComponent } from "../BaseApplicationComponents/ToxnotSignInRequiredComponent";
import { TeamRole } from '../../../store/Models/UserContext';
import SaveDataManager from "../../../utilities/SaveDataManager";
import { SelectedInventoriesManager } from '../../../store/Managers/SelectedInventoriesManager';
import { AppServices } from '../../../AppServices';
import { InventoryIdentifiers, InventoryDetailsResponse, isSurveyReadonly } from '../../../store/StateControllers/Survey/SurveyResponse/SurveyResponseModels';
import ExtensionRequest from '../ExtensionRequest/extensionRequest';
import { DataCollectionAiAgentOperationType, SurveyType, ResponseBomType } from '../../../store/Models/Surveys/SurveyDataModel';
import CommonModalEditor from '../Common/CommonModalEditor';
import AIButton from '../../common/AIButton';
import { SdsInstructionBanner } from '../SdsInstructionBanner';

import './styles.css';

type ProductOverviewProps =
    ToxnotComponentProperties &
    typeof SurveyResponseController.actionCreators &
    SurveyResponseState &
    { aiSurveyAgentState?: AISurveyAgentState };

interface LocalState {
    isButtonDisabled?: boolean;
    isCheckBoxesDisabled?: boolean;
}

class ProductOverview extends ToxnotSignInRequiredComponent<ProductOverviewProps, LocalState> {
    public constructor(props: ProductOverviewProps) {
        super(props);
        this.state = { isButtonDisabled: false, isCheckBoxesDisabled: false };
    }

    protected getDisallowedRoles(): TeamRole[] {
        return [TeamRole.Marketing, TeamRole.Viewer];
    }

    private setLocalStorageItems() {
        localStorage.setItem("select-inventories-action", "surveyResponseImport");
        localStorage.setItem("import-survey-response-id", this.props.responseId!);
        localStorage.setItem("import-survey-response-inventory-id", this.props.selectedInventory?.inventoryId!);
        localStorage.setItem("import-survey-response-return-url", window.location.href);
    }

    private handleClickImportMaterial = () => {
        //localStorage.setItem("customer-last-active-tab", "current-tab"); //todo : find the current tab
        this.setLocalStorageItems();

        SelectedInventoriesManager.navigateToSelectInventoriesPage(this.getUserContextForSignedInUser().userId, this.localizeString("Select Products and Materials"));
    }

    public disableButton = (isButtonDisabled: boolean) => {
        this.setState({ isButtonDisabled: isButtonDisabled });
    };
    public disableCheckBoxes = (isCheckBoxesDisabled: boolean) => {
        this.setState({ isCheckBoxesDisabled: isCheckBoxesDisabled });
    };

    private hasResponseBeenSubmittedOrIsBeingSubmitted = (): boolean => (
        isSurveyReadonly(this.props.coreData, getActiveAIOperationFromState(this.props.aiSurveyAgentState, DataCollectionAiAgentOperationType.Survey)) || (this.props.coreData?.response.responseDataJson && JSON.parse(this.props.coreData.response.responseDataJson).IsOfflineProcessStarted)
    );

    protected renderConcreteComponent(): JSX.Element | null {
        if (!this.props.selectedInventory) {
            return null;
        }

        return (
            <>
                {/* SDS Instruction Text */}
                {this.props.coreData?.request?.sdsRequested && 
                    <SdsInstructionBanner 
                        fileList={this.props.selectedInventory?.fileList}
                        pageType="ProductOverview"
                    />
                }
                <div className="page-title-border m-b-10 flex-space-between">
                    <h4 className="m-t-10 m-b-10">{this.localizeString("Product Overview")}</h4>
                    <div className="m-b-10 import-container">
                        <div className="m-b-10">
                            <AIButton pageContext="material" className="m-r-5" />
                            {this.props.selectedInventory?.hasBomPage &&
                                <>
                                    <button className="btn btn-default m-r-5 import-button" onClick={() => { $("#import-btns-modal-product-overview").modal("show"); }}>{this.localizeString("Import")}
                                        <span className="import-button-icon glyphicon glyphicon-download-alt">
                                        </span>
                                    </button>
                                    <CommonModalEditor modalId={"import-btns-modal-product-overview"} localizer={this.props.appServices} responseId={this.props.responseId} selectedInventory={this.props.selectedInventory} userId={this.getUserContextForSignedInUser().userId} navContext={undefined} handleClickImportMaterial={this.handleClickImportMaterial}></CommonModalEditor>
                                </>
                            }
                            {this.props.coreData?.request?.allowExtensionRequest && this.props.selectedInventory?.revisionRequest == null &&
                                this.props.coreData?.request?.surveyType !== SurveyType.RequestMaterialData_AllowSupplierToSpecifyMaterials &&
                                Object.entries(this.props.coreData?.response?.groupedResponseInventories).every(g => g[1].groupInventoryId !== this.props.selectedInventory?.inventoryId) &&
                                <ExtensionRequest
                                    inventoryId={this.props.selectedInventory?.inventoryId}
                                    extensionRequest={this.props.coreData?.extensionRequest}
                                    localizer={this.props.appServices}
                                    responseId={this.props.coreData?.response.responseId}
                                    responseOwnerId={this.props.coreData?.response.ownerId}
                                    updateExtensionRequest={this.props.setExtensionRequestAndValidateProduct}
                                    setSupplierActionUnavailable={this.props.setSupplierActionUnavailable}
                                    isButtonDisabled={this.state.isButtonDisabled}
                                    disableCheckBoxes={this.disableCheckBoxes}
                                >
                                </ExtensionRequest>
                            }
                        </div>
                    </div>
                </div>
                <ProductDetails
                    appServices={this.props.appServices}
                    onOverviewChange={(name, description, fileId) => this.props.setSurveySelectedInventoryProductDetails(name, description, fileId)}
                    selectedInventory={this.props.selectedInventory}
                    inventoryId={this.props.selectedInventory?.inventoryId}
                    fileId={this.props.selectedInventory?.photoFileId ?? ""}
                    additionalColumnIdIdentifiers={this.props.coreData?.request?.metadata.additionalColumnIdIdentifiers ?? []}
                    requestableColumnIdToFriendlyNameMap={this.props.coreData?.requestableColumnIdToFriendlyNameMap ?? {}}
                    requestorTargetLibraryInventoryIdentifiers={this.props.coreData?.requestorTargetLibraryInventoryIdentifiers ?? []}
                    surveyState={this.props.coreData?.request?.state ?? -1}
                    bomType={this.props.coreData?.response?.bomType}
                    optionalQuestionsInventoryIds={this.props.coreData?.extensionRequest.inventoryIds}
                    responseId={this.props.responseId}
                    updateValidationResult={this.props.updateValidationResult}
                    setSupplierActionUnavailable={this.props.setSupplierActionUnavailable}
                    disableButton={this.disableButton}
                    isCheckBoxesDisabled={this.state.isCheckBoxesDisabled || this.hasResponseBeenSubmittedOrIsBeingSubmitted()}
                />
            </>
        )
    }
}

export default connect(
    (state: ApplicationState) => ({ ...state.surveyResponseState, aiSurveyAgentState: state.aiSurveyAgentState, appServices: state.appServices }),
    SurveyResponseController.actionCreators
)(ProductOverview as any);

interface Props {
    selectedInventory: InventoryDetailsResponse;
    fileId: string | null;
    inventoryId: string | undefined;
    appServices: AppServices;
    onOverviewChange: (name: string, description: string, fileId: string) => void;
    additionalColumnIdIdentifiers: string[];
    requestableColumnIdToFriendlyNameMap: { [columnId: string]: string };
    requestorTargetLibraryInventoryIdentifiers: InventoryIdentifiers[];
    surveyState: number;
    bomType: ResponseBomType | undefined;
    optionalQuestionsInventoryIds: string[] | undefined;
    responseId: string;
    updateValidationResult: (inventoryIds: string[], validationType: string) => void;
    setSupplierActionUnavailable: (result: Result) => void;
    disableButton: (isButtonDisabled: boolean) => void;
    isCheckBoxesDisabled?: boolean;
}

interface ProductDetails {
    name: string;
    description: string | null;
    fileId: string | null;
    inventoryId: string | undefined;
}

const ProductDetails: React.FC<Props> = ({ selectedInventory, inventoryId, fileId, appServices, onOverviewChange, additionalColumnIdIdentifiers, requestableColumnIdToFriendlyNameMap, requestorTargetLibraryInventoryIdentifiers, surveyState, bomType, optionalQuestionsInventoryIds, responseId, updateValidationResult, setSupplierActionUnavailable, disableButton, isCheckBoxesDisabled }) => {
    const [productName, setProductName] = useState(selectedInventory.name || "");
    const [productDescription, setProductDescription] = useState(selectedInventory.description || "");
    const [productFileId, setProductFileId] = useState(fileId || "");
    const [productInventoryId] = useState(inventoryId);
    const [isUploading, setIsUploading] = useState(false);
    const serviceClient = new ToxnotServiceClient();

    const saveInventory = async (productDetails: ProductDetails) => {
        const result = await serviceClient.updateInventoryDetails(responseId, productDetails.inventoryId ?? "", productDetails.name, productDetails.description ?? "", productDetails.fileId ?? "");
        if (!result.success && SurveyResponseController.isSupplierActionUnavailableResult(result)) {
            setSupplierActionUnavailable(result);
            return true;
        }

        return result.success;
    };

    const inventorySaveDataManager: SaveDataManager<ProductDetails> = new SaveDataManager(saveInventory, appServices);

    inventorySaveDataManager.enable();


    const handleNameChange = async (value: string) => {
        if (!productInventoryId || value === productName) return;

        setProductName(value);

        inventorySaveDataManager.save({
            inventoryId: inventoryId,
            name: value,
            description: productDescription,
            fileId: productFileId
        });

        onOverviewChange(
            value,
            productDescription,
            productFileId
        );
    }

    const handleDescriptionChange = async (value: string) => {
        if (!productInventoryId || value === productDescription) return;

        setProductDescription(value);

        inventorySaveDataManager.save({
            inventoryId: inventoryId,
            name: productName,
            description: value,
            fileId: productFileId
        });

        onOverviewChange(
            productName,
            value,
            productFileId
        );
    }

    const handleFileUpload = async (e: any) => {
        if (!inventoryId) return;

        const file = e.target.files[0];


        if (!file.type.startsWith('image/')) {
            setIsUploading(false);
            return;
        }

        setIsUploading(true);

        if (productFileId) {
            const fileDeleteResult = await serviceClient.uploadInventoryFile("", "", 3, false, false, file, 0, inventoryId, true, productFileId);

            if (!fileDeleteResult.success) {
                appServices.displayApiErrorMessage();
                setIsUploading(false);
                return;
            }
        }

        const fileUploadResult = await serviceClient.uploadInventoryFile(file.name, "", 3, false, false, file, 0, inventoryId);

        if (!fileUploadResult || !fileUploadResult.data || fileUploadResult.data.fileId === null || !fileUploadResult.success) {
            appServices.displayApiErrorMessage();
            setIsUploading(false);
            return;
        }

        setProductFileId(fileUploadResult.data.fileId);

        if (fileUploadResult.success) {
            inventorySaveDataManager.save({
                inventoryId: inventoryId,
                name: productName,
                description: productDescription,
                fileId: fileUploadResult.data.fileId
            });
        }

        onOverviewChange(productName, productDescription, fileUploadResult.data.fileId);

        setIsUploading(false);
    }

    const handleClickUpload = () =>
        (document.getElementById('inventory-photo-file-input') as HTMLInputElement)?.click();

    const inventoryIdentifier = requestorTargetLibraryInventoryIdentifiers.firstOrDefault(i => i.inventoryId === selectedInventory.inventoryId);

    const someProductIdentifiersAreNotEmpty = inventoryIdentifier !== null
        ? additionalColumnIdIdentifiers.some(id => SurveyStateController.additionalIdentityColumnsIds[id](inventoryIdentifier) !== "")
        : false;

    const [selectedOption, setSelectedOption] = useState(selectedInventory.validationType == "2" ? "no_longer_available" : selectedInventory.validationType == "3" ? "never_sold" : "unChecked");

    disableButton(selectedOption != "unChecked");

    const handleCheckboxChange = (option: string) => {
        setSelectedOption(prev => {
            const newValue = prev === option ? 'unChecked' : option;
            disableButton(newValue != "unChecked");
            updateValidationResult([productInventoryId!], newValue);
            return newValue;
        });
    };

    const showProductAvailability = bomType !== undefined && bomType !== ResponseBomType.Open;
    return (
        <div className="row m-0">
            <div className="col-xs-12 col-sm-6 col-lg-8 p-0 p-r-15">
                <div className="grouped-data">
                    {someProductIdentifiersAreNotEmpty &&
                        <div className="group">
                            <div className="title">{appServices.localizeString("Requested Product Information")}</div>
                            <div className="content">
                                {additionalColumnIdIdentifiers.map(id =>
                                    <div key={id}>
                                        <b>{appServices.localizeString(requestableColumnIdToFriendlyNameMap[id]) ?? appServices.localizeString("Unknown")}: </b>
                                        <span>{SurveyStateController.additionalIdentityColumnsIds[id](inventoryIdentifier!)}</span> {/* inventoryIdentifier cannot be null here as if it's null the entire group div will not be rendered due to someProductIdentifiersAreNotEmpty being false */}
                                    </div>
                                )}
                            </div>
                        </div>
                    }

                    {showProductAvailability &&
                        <div className="group">
                            <div className="title">{appServices.localizeString("Product Availability")}</div>
                            <div className="form-group">
                                <label className="checkbox-label">
                                    <input type="checkbox" disabled={isCheckBoxesDisabled} checked={selectedOption === "no_longer_available"} onChange={() => handleCheckboxChange("no_longer_available")} />
                                    {appServices.localizeString("This product is no longer available.")}
                                    <span className="tooltip">
                                        {appServices.localizeString("My company previously sold this product, but it is now discontinued.")}
                                    </span>
                                </label>
                            </div>

                            <div className="form-group">
                                <label className="checkbox-label">
                                    <input type="checkbox" disabled={isCheckBoxesDisabled} checked={selectedOption === "never_sold"} onChange={() => handleCheckboxChange("never_sold")} />
                                    {appServices.localizeString("This product was never sold by my company.")}
                                    <span className="tooltip">
                                        {appServices.localizeString("My company has never supplied this product and should not be listed as a supplier.")}
                                    </span>
                                </label>
                            </div>
                        </div>
                    }
                    <div className="group">
                        <div className="title">{appServices.localizeString("Manufacturer Description")}</div>
                        <div className="content">
                            <div className="form-group">
                                <label>{appServices.localizeString("Material Name")}</label>
                                <TextInput onValueChange={e => handleNameChange(e)} value={productName} />
                            </div>
                            <div className="form-group">
                                <label>{appServices.localizeString("Material Description")}</label>
                                <TextAreaInput onValueChange={e => handleDescriptionChange(e)} value={productDescription} />
                            </div>
                        </div>
                    </div>
                </div>
            </div>
            <div className="col-xs-12 col-sm-6 col-lg-4 p-0 p-l-15">
                <div className="form-group">
                    <label>{appServices.localizeString("Material Photo")}</label>
                    {!productFileId ?
                        (<p className="no-photo-message">
                            {appServices.localizeString("No photo uploaded.")}
                        </p>)
                        :
                        (<div className="photo-container m-b-15">
                            <img
                                className={'product-image-size'}
                                src={"/Api/InventoryFile?inventoryId=" + encodeURIComponent(inventoryId || "") + "&fileId=" + encodeURIComponent(productFileId) + "&attachment=false"}
                            />
                            {isUploading && (
                                <div className="alert alert-info">
                                    {appServices.localizeString("Uploading....")}
                                </div>
                            )}
                        </div>
                        )}
                    <div>
                        <button type="button" className="btn btn-default inventory-upload-photo-button"
                            onClick={handleClickUpload}>{appServices.localizeString("Upload Photo")}</button>
                        <input type="file" id="inventory-photo-file-input" style={{ display: 'none' }}
                            accept=".jpg,.jpeg,.gif,.png,image/jpeg,image/gif,image/png"
                            onChange={async (e) => await handleFileUpload(e)} />
                    </div>
                </div>
            </div>
        </div>
    );
};
